/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";
import React from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import AppProfileMenu from "components/AppProfileMenu";
import StorageLicenseBar from "components/StorageLicenseBar";
import MicrolabLogo from "components/MicrolabLogo";
import MenuToggleIcon from "components/MenuToggleIcon";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import {
  collapseItem,
  collapseIconBox,
  collapseText,
} from "examples/Sidenav/styles/sidenavCollapse";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setDarkMode,
} from "context";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { resetAuth } from "../../redux/slices/authSlice";
import { setLang } from "../../redux/slices/generalSlice";
import { store } from "../../redux/store";
import { performLogout } from "../../utils/logout";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import { Padding } from "@mui/icons-material";
import { useUpcomingDemos } from "../../context/UpcomingDemosContext";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import Tooltip from "@mui/material/Tooltip";
import Backdrop from "@mui/material/Backdrop";
import useMediaQuery from "@mui/material/useMediaQuery";
import Switch from "@mui/material/Switch";

const LANGUAGES = [
  "en", "fr", "ko", "pt", "pt-br", "es", "it", "ge", "nl", "zh-cn", "zh-tw", "ja",
  "ar", "kk", "tk", "tr", "fa", "hi", "id", "ro", "ru", "vi", "ms", "tl", "no", "da", "sv", "el", "pl", "cs", "fi", "hu",
];

function Sidenav({
  color,
  university = null,
  brand,
  brandName,
  routes,
  ...rest
}) {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    sidenavColor,
  } = controller;
  const location = useLocation();
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const lang = useSelector((state) => state.general?.lang) || "en";
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);
  const collapseName = location.pathname.replace("/", "");

  /*
    Upcoming demos badge: live count + click-to-open modal on the
    "Demo requests" sidebar row. Provider returns enabled=false for
    non-admins so the badge silently hides for everyone else.
  */
  const upcomingDemos = useUpcomingDemos();

  // Expand/collapse state for parent items with nested routes (e.g. Quiz)
  const [openCollapse, setOpenCollapse] = useState(() => {
    const initial = {};
    routes.forEach((r) => {
      if (r.collapse && Array.isArray(r.collapse) && r.collapse.length > 0) {
        const isActive = r.collapse.some((c) => c.route && location.pathname.startsWith(c.route));
        initial[r.key] = !!isActive;
      }
    });
    return initial;
  });

  const toggleCollapse = (key) => {
    setOpenCollapse((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }
  const closeSidenav = () => setMiniSidenav(dispatch, true);
  // Below xl the sidenav is a slide-over sheet opened from DashboardNavbar.
  const isOverlay = useMediaQuery((theme) => theme.breakpoints.down("xl"));
  const overlayOpen = isOverlay && !miniSidenav;

  // Mobile: close the sheet after navigating so the new page is visible.
  useEffect(() => {
    if (isOverlay) setMiniSidenav(dispatch, true);
  }, [location.pathname]);

  // Mobile: lock page scroll behind the open sheet.
  useEffect(() => {
    if (!overlayOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [overlayOpen]);

  const getNavLabel = (navKey, fallbackName) => {
    const translated = t(`nav.${navKey}`);
    return translated === `nav.${navKey}` ? fallbackName : translated;
  };

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : transparentSidenav
      );
      setWhiteSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : whiteSidenav
      );
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
    // Do not depend on `location`: only viewport width matters; re-running on every route caused extra work.
  }, [dispatch]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map((route) => {
    if (route.hideInSidenav) return null;
    const { type, name, icon, title, noCollapse, key, href, route: routePath, collapse } = route;
    let returnValue;

    if (type === "collapse" && collapse && Array.isArray(collapse) && collapse.length > 0) {
      const isOpen = openCollapse[key] !== false;
      const isActive = collapse.some((c) => c.route && location.pathname === c.route);
      returnValue = (
        <React.Fragment key={key}>
          <ListItem
            component="li"
            disablePadding
            onClick={() => toggleCollapse(key)}
            sx={{ cursor: "pointer" }}
          >
            <MDBox
              sx={(theme) =>
                collapseItem(theme, {
                  active: isActive,
                  transparentSidenav,
                  whiteSidenav,
                  darkMode,
                  sidenavColor,
                })
              }
              width="100%"
              display="flex"
              alignItems="center"
            >
              <ListItemIcon
                sx={(theme) => ({
                  ...collapseIconBox(theme, { transparentSidenav, whiteSidenav, darkMode, active: isActive }),
                  "& .MuiIcon-root": { color: "white !important" },
                  "& svg": { color: "white !important" },
                })}
              >
                {typeof icon === "string" ? <Icon fontSize="small">{icon}</Icon> : icon}
              </ListItemIcon>
              <ListItemText
                primary={getNavLabel(key, name)}
                sx={(theme) =>
                  collapseText(theme, {
                    miniSidenav,
                    transparentSidenav,
                    whiteSidenav,
                    active: isActive,
                  })
                }
              />
              <Icon
                sx={{
                  fontSize: "1.25rem",
                  color: "white",
                  transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.2s",
                }}
              >
                expand_more
              </Icon>
            </MDBox>
          </ListItem>
          {isOpen &&
            collapse.map((child) => (
              <NavLink key={child.key} to={child.route} style={{ textDecoration: "none" }}>
                <SidenavCollapse
                  name={getNavLabel(child.key, child.name)}
                  icon={<Icon sx={{ fontSize: "0.75rem", opacity: 0.7 }}>chevron_right</Icon>}
                  active={location.pathname === child.route}
                  noCollapse
                  nested
                />
              </NavLink>
            ))}
        </React.Fragment>
      );
    } else if (type === "collapse") {
      const isActive = routePath
        ? location.pathname === routePath || location.pathname.startsWith(routePath + "/")
        : key === collapseName;
      const navNode = href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            name={getNavLabel(key, name)}
            icon={icon}
            active={isActive}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink key={key} to={routePath}>
          <SidenavCollapse
            name={getNavLabel(key, name)}
            icon={icon}
            active={isActive}
          />
        </NavLink>
      );

      /*
        Upcoming-demos badge: overlay a click-target on the right edge of
        the "Demo requests" row. The badge stops propagation so clicking
        it opens the modal instead of navigating; tapping the rest of the
        row still navigates as before.
      */
      const showDemoBadge =
        key === "demo-requests" &&
        upcomingDemos.enabled &&
        Number(upcomingDemos.total || 0) > 0;
      if (showDemoBadge) {
        const count = Number(upcomingDemos.total || 0);
        const todayCount = Number(upcomingDemos.todayCount || 0);
        const badgeLabel =
          todayCount > 0
            ? `${todayCount} today${count > todayCount ? ` · ${count} total` : ""}`
            : `${count} upcoming`;
        const isUrgent = todayCount > 0;
        const handleBadgeClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          upcomingDemos.openModal();
        };
        returnValue = (
          <MDBox key={key} sx={{ position: "relative" }}>
            {navNode}
            <Tooltip title={`${badgeLabel} — click to view`} placement="right">
              <MDBox
                onClick={handleBadgeClick}
                sx={(theme) => ({
                  position: "absolute",
                  right: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: miniSidenav ? "none" : "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 0.9,
                  py: 0.25,
                  borderRadius: 999,
                  cursor: "pointer",
                  zIndex: 2,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: isUrgent ? "#fff" : "#fff",
                  background: isUrgent
                    ? `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.warning.main})`
                    : `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.primary.main})`,
                  boxShadow: isUrgent
                    ? `0 0 0 0 ${theme.palette.error.main}66`
                    : `0 4px 10px ${theme.palette.primary.main}33`,
                  animation: isUrgent ? "demoBadgePulse 1.6s ease-in-out infinite" : "none",
                  "@keyframes demoBadgePulse": {
                    "0%": { boxShadow: `0 0 0 0 ${theme.palette.error.main}66` },
                    "70%": { boxShadow: `0 0 0 6px ${theme.palette.error.main}00` },
                    "100%": { boxShadow: `0 0 0 0 ${theme.palette.error.main}00` },
                  },
                  transition: "transform 0.15s ease",
                  "&:hover": { transform: "translateY(-50%) scale(1.05)" },
                })}
              >
                <EventAvailableIcon sx={{ fontSize: 13, color: "#fff" }} />
                {todayCount > 0 ? `${todayCount} today` : `${count}`}
              </MDBox>
            </Tooltip>
            {/* Mini-sidenav: tiny pulsing dot in the corner of the icon */}
            {miniSidenav ? (
              <MDBox
                onClick={handleBadgeClick}
                sx={(theme) => ({
                  position: "absolute",
                  top: 6,
                  left: 24,
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  cursor: "pointer",
                  zIndex: 2,
                  background: isUrgent
                    ? theme.palette.error.main
                    : theme.palette.info.main,
                  boxShadow: isUrgent
                    ? `0 0 0 0 ${theme.palette.error.main}80`
                    : `0 0 0 2px ${theme.palette.background.paper || "#fff"}`,
                  animation: isUrgent ? "demoBadgeDot 1.6s ease-in-out infinite" : "none",
                  "@keyframes demoBadgeDot": {
                    "0%": { boxShadow: `0 0 0 0 ${theme.palette.error.main}80` },
                    "70%": { boxShadow: `0 0 0 5px ${theme.palette.error.main}00` },
                    "100%": { boxShadow: `0 0 0 0 ${theme.palette.error.main}00` },
                  },
                })}
              />
            ) : null}
          </MDBox>
        );
      } else {
        returnValue = navNode;
      }
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  return (
    <>
    <Backdrop
      open={overlayOpen}
      onClick={closeSidenav}
      sx={(theme) => ({
        zIndex: theme.zIndex.drawer - 1,
        backgroundColor: "rgba(2, 6, 23, 0.5)",
        backdropFilter: "blur(2px)",
        display: { xl: "none" },
      })}
    />
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox
        display="flex"
        flexDirection="column"
        height="100%"
        maxHeight="100%"
        minHeight={0}
        overflow="hidden"
        flex={1}
        // Full-width menu on tablets: keep the column at a readable width.
        sx={{ width: "100%", maxWidth: { xs: 640, xl: "none" }, mx: "auto" }}
      >
        {isOverlay ? (
          // Full-screen menu header: lines up with DashboardNavbar so the
          // hamburger appears to turn into the close button in place.
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexShrink={0}
            sx={{ minHeight: 58, mt: 1.25, mx: 1.25, pl: 1.75, pr: 1 }}
          >
            {university?.logo_url ? (
              <MDBox
                component="img"
                src={university.logo_url}
                alt="Brand"
                sx={{ height: 32, width: "auto", maxWidth: "60%", objectFit: "contain", display: "block" }}
              />
            ) : (
              <MDBox component={NavLink} to="/" aria-label="Microlab home" sx={{ lineHeight: 0 }}>
                <MicrolabLogo height={26} textColor="#ffffff" markColor="#ffffff" glyphColor="#03045E" />
              </MDBox>
            )}
            <IconButton
              onClick={closeSidenav}
              disableRipple
              aria-label="Close navigation"
              aria-expanded={!miniSidenav}
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                color: "#fff",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                "&:focus-visible": { outline: "2px solid #fff", outlineOffset: 2 },
              }}
            >
              <MenuToggleIcon open={!miniSidenav} />
            </IconButton>
          </MDBox>
        ) : (
        <MDBox pt={1.5} pb={0.5} px={0.75} textAlign="center" flexShrink={0}>
          <>
            {university?.logo_url ? (
              <MDBox
                component="img"
                src={university?.logo_url}
                alt="Brand"
                sx={{
                  maxHeight: 70,
                  maxWidth: "50%",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  display: "block",
                  mx: "auto",
                  marginTop: 1
                }}
              />
            ) : (
              <MDBox
                component={NavLink}
                to="/"
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  "&:focus, &:focus-visible": { border: "none", outline: "none", boxShadow: "none" },
                }}
              >
                <MDBox
                  component="img"
                  src={require("../../assets/images/logos/reverse-logo.png")}
                  alt="Brand"
                  sx={{ marginTop: 1, width: brandName ? "4rem" : "30%", minWidth: brandName ? "3.5rem" : "auto", maxWidth: "50%", objectFit: "contain" }}
                />
                {brandName ? (
                  <MDBox
                    width="100%"
                    sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
                  >
                    <MDTypography
                      component="h6"
                      variant="button"
                      fontWeight="medium"
                      color={textColor}
                    >
                      {brandName}
                    </MDTypography>
                  </MDBox>
                ) : null}
              </MDBox>
            )}
          </>
        </MDBox>
        )}
        {/* {university?.name && (
        <MDBox px={2} mt={1} mb={0.5} textAlign="center">
          <MDTypography
            variant="caption"
            fontWeight="medium"
            color={textColor}
            sx={{ opacity: 0.8 }}
          >
            {university?.name}
          </MDTypography>
        </MDBox>
      )} */}
        <Divider
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
          sx={{ flexShrink: 0 }}
        />
        <MDBox px={1.5} pt={1} pb={0.5} flexShrink={0}>
          <AppProfileMenu variant="sidenav" collapsed={miniSidenav} />
        </MDBox>
        {isOverlay ? (
          <MDBox px={1.5} pt={0.5} pb={1} flexShrink={0}>
            <StorageLicenseBar variant="compact" />
          </MDBox>
        ) : null}
        <MDBox
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(127,127,127,0.45)",
              borderRadius: 999,
            },
          }}
        >
          <List disablePadding>{renderRoutes}</List>
        </MDBox>
        <MDBox
          px={1.25}
          pt={1.5}
          pb={1.5}
          mt="auto"
          flexShrink={0}
          sx={{
            borderTop: textColor === "white" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
          }}
        >
          {miniSidenav ? (
            <>
              <IconButton
                size="small"
                onClick={(e) => setLangMenuAnchor(e.currentTarget)}
                sx={{
                  color: textColor,
                  "&:hover": { backgroundColor: textColor === "white" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)" },
                }}
                aria-label={t("common.language")}
              >
                <Icon sx={{ fontSize: "1.5rem" }}>language</Icon>
              </IconButton>
              <Menu
                anchorEl={langMenuAnchor}
                open={Boolean(langMenuAnchor)}
                onClose={() => setLangMenuAnchor(null)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                PaperProps={{
                  sx: { mt: 1.5, maxHeight: 480, minHeight: 360, borderRadius: 2, boxShadow: "0 8px 32px rgba(0,0,0,0.24)", minWidth: 220 },
                }}
              >
                {LANGUAGES.map((code) => (
                  <MenuItem
                    key={code}
                    selected={code === lang}
                    onClick={() => {
                      reduxDispatch(setLang(code));
                      setLangMenuAnchor(null);
                    }}
                  >
                    {t(`languages.${code}`)}
                  </MenuItem>
                ))}
              </Menu>
              <Tooltip title={t("common.darkMode")} placement="right">
                <IconButton
                  size="small"
                  onClick={() => setDarkMode(dispatch, !darkMode)}
                  sx={{
                    mt: 0.75,
                    color: darkMode ? "#fbbf24" : textColor,
                    "&:hover": { backgroundColor: textColor === "white" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)" },
                  }}
                  aria-label={t("common.darkMode")}
                  aria-pressed={darkMode}
                >
                  <Icon sx={{ fontSize: "1.5rem" }}>{darkMode ? "light_mode" : "dark_mode"}</Icon>
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <FormControl fullWidth sx={{ mb: 1.5 }}>
              <Select
                value={lang}
                onChange={(e) => reduxDispatch(setLang(e.target.value))}
                displayEmpty
                size="medium"
                renderValue={(v) => (
                  <MDBox display="flex" alignItems="center" gap={1.25}>
                    <Icon sx={{ fontSize: "1.5rem", opacity: 0.95 }}>language</Icon>
                    <MDTypography variant="button" color={textColor} fontWeight="600" sx={{ textTransform: "none", fontSize: "0.875rem" }}>
                      {t(`languages.${v}`) || v}
                    </MDTypography>
                  </MDBox>
                )}
                sx={{
                  color: textColor,
                  borderRadius: 3,
                  minHeight: 48,
                  backgroundColor: textColor === "white" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: textColor === "white" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.12)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: textColor === "white" ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.25)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: textColor === "white" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.4)",
                    borderWidth: "1px",
                  },
                  "& .MuiSvgIcon-root": { color: textColor },
                  fontSize: "0.875rem",
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 1.5,
                      maxHeight: 480,
                      minHeight: 360,
                      borderRadius: 2,
                      boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
                    },
                  },
                }}
              >
                {LANGUAGES.map((code) => (
                  <MenuItem key={code} value={code}>
                    {t(`languages.${code}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!miniSidenav ? (
            <MDBox
              component="label"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1.25}
              sx={{
                minHeight: 48,
                px: 1.5,
                borderRadius: 3,
                cursor: "pointer",
                color: textColor,
                backgroundColor: textColor === "white" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
                border: `1px solid ${textColor === "white" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.12)"}`,
                transition: "border-color 0.2s ease",
                "&:hover": {
                  borderColor: textColor === "white" ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.25)",
                },
              }}
            >
              <MDBox display="flex" alignItems="center" gap={1.25}>
                <Icon sx={{ fontSize: "1.5rem", opacity: 0.95, color: darkMode ? "#fbbf24" : "inherit" }}>
                  {darkMode ? "dark_mode" : "light_mode"}
                </Icon>
                <MDTypography
                  variant="button"
                  color={textColor}
                  fontWeight="600"
                  sx={{ textTransform: "none", fontSize: "0.875rem" }}
                >
                  {t("common.darkMode")}
                </MDTypography>
              </MDBox>
              <Switch
                checked={darkMode}
                onChange={(e) => setDarkMode(dispatch, e.target.checked)}
                inputProps={{ "aria-label": t("common.darkMode") }}
              />
            </MDBox>
          ) : null}
        </MDBox>
        <MDBox pt={1} pb={{ xs: 2, xl: 3.5 }} px={1.25} flexShrink={0}>
          <MDButton
            component="a"
            onClick={performLogout}
            target="_blank"
            rel="noreferrer"
            variant="gradient"
            color={sidenavColor}
            fullWidth
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              py: 1.25,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
          >
            {t("common.logout")}
          </MDButton>
        </MDBox>
      </MDBox>
    </SidenavRoot>
    </>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
